"use client";

import AppButton from "@/components/app-button";
import { EPermission } from "@/constants/permission.enum";
import { ISalaryScale } from "@/features/policies/salary-scales";
import { withRBAC } from "@/lib/action-utils";
import { PermissionDeniedError } from "@/lib/errors";
import { http } from "@/lib/http";
import { formatDate, getBaseResourceUrl } from "@/lib/utils";
import clsx from "clsx";
import { Download, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx-js-style";

interface ExportButtonProps {
  searchCondition?: string;
}

export function ExportButton({ searchCondition }: ExportButtonProps) {
  const t = useTranslations();
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(false);

  const handleExport = async () => {
    try {
      setIsLoading(true);

      const protectedExport = withRBAC({
        requiredPermissions: [EPermission.Export],
        resourcePath: getBaseResourceUrl(pathname),
      })(async () => {
        const params = new URLSearchParams({
          searchCondition: searchCondition || "",
        });
        const response = await http.get<ISalaryScale[]>(
          `v1/payroll/salary-scales/export?${params.toString()}`
        );
        const data = response.data;

        if (!data || data.length === 0) {
          toast.error(t("notification.no-data-to-export"));
          return false;
        }

        const worksheet = XLSX.utils.json_to_sheet(
          data.map((item) => ({
            [t("feature.salary-scale.code")]: item.code ?? "",
            [t("feature.salary-scale.name")]: item.name ?? "",
            [t("feature.salary-scale.base-salary")]: item.baseSalary ?? "",
            [t("feature.salary-scale.effective-date")]: formatDate(
              item.effectiveDate
            ),
            [t("feature.salary-scale.expiration-date")]: formatDate(
              item.expirationDate
            ),
          }))
        );
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "SalaryScales");
        XLSX.writeFile(workbook, `thang_bang_luong_${Date.now()}.xlsx`);
        return true;
      });

      const exported = await protectedExport();
      if (exported) {
        toast.success(t("notification.exported-successfully"));
      }
    } catch (error) {
      if (error instanceof PermissionDeniedError) {
        toast.error(error.message);
      } else {
        toast.error(t("notification.export-failed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppButton
      variant="outline-secondary"
      className={clsx(isLoading && "opacity-50")}
      onClick={handleExport}
      disabled={isLoading}
    >
      {isLoading ? (
        <div className="flex h-6 w-6 items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <Download />
      )}
      <span>{t("action.export.excel")}</span>
    </AppButton>
  );
}
